import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InvestmentsAPI, Investment } from '../services/investments.ts';
import { formatCurrency, getCurrencyLocale } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import { ArrowLeftIcon, PencilIcon, DocumentTextIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const InvestmentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<Investment | null>(null);

  // Mapear categorias para rótulos em português
  const categoryLabels: Record<string, string> = {
    crypto: 'Cripto',
    offshore: 'Offshore',
    stocks: 'Ações',
    fixed_income: 'Renda Fixa',
    funds: 'Fundos',
    other: 'Outros',
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      const data = await InvestmentsAPI.get(id);
      setItem(data);
    };
    load();
  }, [id]);

  if (!item) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/investments')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar
          </button>

          <h1 className="text-3xl font-bold text-gray-900">{item.description}</h1>
          <p className="text-gray-600 mt-2">Detalhes do investimento</p>
        </div>

        {/* Informações Básicas */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
            Informações Básicas
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Categoria</label>
              <p className="text-lg font-semibold text-gray-900">{categoryLabels[item.category] || item.category}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                {formatStatus(item.status)}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Moeda</label>
              <p className="text-lg font-semibold text-gray-900">{item.currency || 'BRL'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Criado em</label>
              <p className="text-lg font-semibold text-gray-900">{item.createdAt ? new Date(item.createdAt).toLocaleDateString('pt-BR') : 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Valores */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
            Valores
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor Investido</label>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(item.investedValue, item.currency || 'BRL', getCurrencyLocale(item.currency || 'BRL'))}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Valor Atual</label>
              <p className="text-lg font-semibold text-gray-900">{formatCurrency(item.currentValue, item.currency || 'BRL', getCurrencyLocale(item.currency || 'BRL'))}</p>
            </div>
            <div className="md:col-span-2">
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Rendimento</label>
                <p className="text-lg font-semibold text-gray-900">{formatCurrency(item.yieldAmount, item.currency || 'BRL', getCurrencyLocale(item.currency || 'BRL'))} ({item.yieldPercent.toFixed(2)}%)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 mt-8">
          <button
            type="button"
            onClick={() => navigate(`/investments/${item.id}/edit`)}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center"
          >
            <PencilIcon className="h-5 w-5 mr-2" />
            Editar Investimento
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvestmentDetail;
